(function () {
  const header = document.querySelector('[data-sunco-header]');
  if (!header) return;

  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const headerSection = header.closest('.section-sunco-header') || header;
  const headerGroup = headerSection.closest('#header-group');

  function getAnnouncementSection() {
    const announcementBar = headerGroup?.querySelector('.announcement-bar');
    return announcementBar?.closest('.shopify-section') || announcementBar?.parentElement || null;
  }

  function isDetachedStickyState() {
    return header.dataset.suncoStickyVisible === 'true' || header.dataset.suncoStickyExit === 'true';
  }

  function updateHeaderHeight() {
    const hasReservedFlow = headerGroup?.dataset.suncoStickyFlowReserved === 'true';
    if (!isDetachedStickyState() && hasReservedFlow && window.scrollY > 1) return;

    const headerHeight = Math.ceil(headerSection.getBoundingClientRect().height);
    const announcementHeight = Math.ceil(getAnnouncementSection()?.getBoundingClientRect().height || 0);
    const stackHeight = headerHeight + announcementHeight;
    if (!headerHeight || !stackHeight) return;

    document.body.style.setProperty('--header-height', `${stackHeight}px`);
    document.body.style.setProperty('--header-group-height', `${stackHeight}px`);
    headerGroup?.style.setProperty('--sunco-sticky-announcement-height', `${announcementHeight}px`);
    headerGroup?.style.setProperty('--sunco-sticky-stack-height', `${stackHeight}px`);

    if (!isDetachedStickyState()) {
      headerGroup?.style.setProperty('--sunco-header-flow-height', `${stackHeight}px`);
    }
  }

  function scheduleHeaderHeightUpdate() {
    window.requestAnimationFrame(updateHeaderHeight);
  }

  function setupStickyStateEffects() {
    const enterAnimationMs = 220;
    const exitAnimationMs = 180;
    const mobileRevealDistance = 56;
    const desktopRevealDistance = 112;
    const hideDistance = 24;
    const gestureResetMs = 180;
    let wasActive = header.dataset.suncoStickyVisible === 'true';
    let thresholdStickyVisible = wasActive;
    let lastScrollY = Math.max(0, window.scrollY);
    let lastMovementAt = 0;
    let upwardDistance = 0;
    let downwardDistance = 0;
    let thresholdFrame = 0;
    let enterTimeout;
    let exitTimeout;

    const usesScrollThreshold = () => header.getAttribute('sticky') === 'scroll-up';
    const getRevealDistance = () => mobileQuery.matches ? mobileRevealDistance : desktopRevealDistance;

    const reserveHeaderFlow = () => {
      if (!headerGroup) return;

      headerGroup.dataset.suncoStickyFlowReserved = 'true';
    };

    const clearEnterState = () => {
      if (enterTimeout) window.clearTimeout(enterTimeout);
      enterTimeout = undefined;
      delete header.dataset.suncoStickyEnter;
    };

    const clearExitState = () => {
      if (exitTimeout) window.clearTimeout(exitTimeout);
      exitTimeout = undefined;
      delete header.dataset.suncoStickyExit;
    };

    const reflectStickyState = () => {
      const stickyState = header.dataset.stickyState;
      const isActive = usesScrollThreshold() ? thresholdStickyVisible : stickyState === 'active';

      if (isActive) {
        reserveHeaderFlow();
        clearExitState();
        header.dataset.suncoStickyVisible = 'true';

        if (!wasActive) {
          clearEnterState();
          header.dataset.suncoStickyEnter = 'true';
          enterTimeout = window.setTimeout(() => {
            delete header.dataset.suncoStickyEnter;
            enterTimeout = undefined;
          }, enterAnimationMs);
        }
      } else {
        delete header.dataset.suncoStickyVisible;
        clearEnterState();

        if (wasActive && stickyState === 'idle') {
          clearExitState();
          header.dataset.suncoStickyExit = 'true';
          exitTimeout = window.setTimeout(() => {
            delete header.dataset.suncoStickyExit;
            exitTimeout = undefined;
            reserveHeaderFlow();
            scheduleHeaderHeightUpdate();
          }, exitAnimationMs);
        } else if (stickyState !== 'idle') {
          clearExitState();
          reserveHeaderFlow();
        }
      }

      wasActive = isActive;
      updateHeaderHeight();
      scheduleHeaderHeightUpdate();
    };

    const resetThresholdDistances = () => {
      upwardDistance = 0;
      downwardDistance = 0;
    };

    const updateScrollThreshold = () => {
      thresholdFrame = 0;

      const currentScrollY = Math.max(0, window.scrollY);
      const delta = currentScrollY - lastScrollY;
      const movementAt = window.performance.now();

      if (!usesScrollThreshold()) {
        resetThresholdDistances();
        lastMovementAt = 0;
        lastScrollY = currentScrollY;
        reflectStickyState();
        return;
      }

      if (currentScrollY <= 0) {
        resetThresholdDistances();
        lastMovementAt = 0;
        thresholdStickyVisible = false;
        lastScrollY = currentScrollY;
        reflectStickyState();
        return;
      }

      if (delta !== 0 && movementAt - lastMovementAt > gestureResetMs) {
        resetThresholdDistances();
      }

      if (delta !== 0) lastMovementAt = movementAt;

      if (delta < 0) {
        upwardDistance += Math.abs(delta);
        downwardDistance = 0;

        if (
          !thresholdStickyVisible
          && upwardDistance >= getRevealDistance()
          && header.dataset.stickyState === 'active'
        ) {
          thresholdStickyVisible = true;
          upwardDistance = 0;
          reflectStickyState();
        }
      } else if (delta > 0) {
        downwardDistance += delta;
        upwardDistance = 0;

        if (thresholdStickyVisible && downwardDistance >= hideDistance) {
          thresholdStickyVisible = false;
          downwardDistance = 0;
          reflectStickyState();
        }
      }

      lastScrollY = currentScrollY;
    };

    const scheduleScrollThresholdUpdate = () => {
      if (thresholdFrame) return;
      thresholdFrame = window.requestAnimationFrame(updateScrollThreshold);
    };

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.attributeName === 'data-sticky-state')) {
        reflectStickyState();
      }
    });

    observer.observe(header, { attributes: true, attributeFilter: ['data-sticky-state'] });

    window.addEventListener('scroll', scheduleScrollThresholdUpdate, { passive: true });

    reflectStickyState();
  }

  function closeDrawer() {
    const details = header.querySelector('#Details-menu-drawer-container');
    if (details instanceof HTMLDetailsElement) {
      details.removeAttribute('open');
    }
  }

  function setupDrawer() {
    header.querySelectorAll('[data-sunco-drawer-close]').forEach((trigger) => {
      trigger.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDrawer();
    });
  }

  function setupSearchTrigger() {
    header.querySelectorAll('[data-sunco-search-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const modal = document.querySelector('#search-modal');
        if (modal && typeof modal.showDialog === 'function') {
          modal.showDialog();
          return;
        }

        const dialog = modal?.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement) {
          dialog.showModal();
        }
      });
    });
  }

  function loadDeferredMenuImages(container) {
    container.querySelectorAll('img[data-sunco-header-lazy-image]').forEach((image) => {
      if (image.dataset.suncoHeaderImageLoaded === 'true') return;

      const src = image.dataset.suncoSrc;
      const srcset = image.dataset.suncoSrcset;

      if (srcset) image.setAttribute('srcset', srcset);
      if (src) image.setAttribute('src', src);

      image.dataset.suncoHeaderImageLoaded = 'true';
    });
  }

  function setupDesktopMenus() {
    header.querySelectorAll('[data-sunco-menu-item]').forEach((item) => {
      const trigger = item.querySelector('[data-sunco-menu-trigger]');
      const panel = item.querySelector('[data-sunco-menu-panel]');
      if (!trigger || !panel) return;

      const setOpen = (open) => {
        if (open) loadDeferredMenuImages(panel);
        item.classList.toggle('is-open', open);
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      item.addEventListener('mouseenter', () => {
        if (!mobileQuery.matches) setOpen(true);
      });

      item.addEventListener('mouseleave', () => {
        if (!mobileQuery.matches) setOpen(false);
      });

      item.addEventListener('focusin', () => {
        if (!mobileQuery.matches) setOpen(true);
      });

      item.addEventListener('focusout', (event) => {
        if (!mobileQuery.matches && !item.contains(event.relatedTarget)) {
          setOpen(false);
        }
      });
    });
  }

  setupDrawer();
  setupSearchTrigger();
  setupDesktopMenus();
  setupStickyStateEffects();
  updateHeaderHeight();

  const resizeObserver = new ResizeObserver(scheduleHeaderHeightUpdate);
  resizeObserver.observe(header);
  let observedAnnouncementSection;

  function observeAnnouncementSection() {
    const announcementSection = getAnnouncementSection();
    if (!announcementSection || announcementSection === observedAnnouncementSection) return;

    if (observedAnnouncementSection) resizeObserver.unobserve(observedAnnouncementSection);
    resizeObserver.observe(announcementSection);
    observedAnnouncementSection = announcementSection;
  }

  observeAnnouncementSection();

  if (headerGroup) {
    const headerGroupObserver = new MutationObserver(() => {
      observeAnnouncementSection();
      scheduleHeaderHeightUpdate();
    });
    headerGroupObserver.observe(headerGroup, { childList: true, subtree: true });
  }

  window.addEventListener('resize', scheduleHeaderHeightUpdate);
  window.addEventListener('load', scheduleHeaderHeightUpdate);
})();
